import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { CopyToClipboard } from '@axilon-apps/libs-ui-components'
import type { EntitiesCreateUserSessionResponse } from '@axilon-apps/domains-identity/state'
import type { IngestIntake } from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../../components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../../components/ui/select'
import { API_KEY_TOKEN_PLACEHOLDER, buildIntakeUploadCurl } from './copy-curl'

interface IssuedTokenDialogProps {
  open: boolean
  keyName: string
  issuedToken: EntitiesCreateUserSessionResponse | null
  intakes: IngestIntake[]
  showIntakeCommand?: boolean
  proxyApiV1Url: string
  onClose: () => void
}

export function IssuedTokenDialog({
  open,
  keyName,
  issuedToken,
  intakes,
  showIntakeCommand = true,
  proxyApiV1Url,
  onClose
}: IssuedTokenDialogProps) {
  const [selectedIntake, setSelectedIntake] = useState('')
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null)
  const showOnce = issuedToken !== null
  const canClose = !showOnce || copiedTokenId === issuedToken.id
  const intakeNames = intakes.map((intake) => intake.name).join('\u0000')

  useEffect(() => {
    if (!open) return
    setCopiedTokenId(null)
  }, [issuedToken?.id, open])

  useEffect(() => {
    if (!open) return
    setSelectedIntake((current) =>
      intakes.some((intake) => intake.name === current) ? current : (intakes[0]?.name ?? '')
    )
  }, [intakeNames, intakes, open])

  const command = useMemo(
    () =>
      selectedIntake
        ? buildIntakeUploadCurl({
            proxyApiV1Url,
            intakeName: selectedIntake,
            token: issuedToken?.accessToken ?? API_KEY_TOKEN_PLACEHOLDER
          })
        : '',
    [issuedToken?.accessToken, proxyApiV1Url, selectedIntake]
  )

  const close = () => {
    if (!canClose) return
    setCopiedTokenId(null)
    setSelectedIntake('')
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        className="max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] min-w-0 gap-0 overflow-hidden p-0 sm:max-w-2xl"
        showCloseButton={canClose}
        onEscapeKeyDown={(event) => {
          if (!canClose) event.preventDefault()
        }}
        onPointerDownOutside={(event) => {
          if (!canClose) event.preventDefault()
        }}
      >
        <DialogHeader className="min-w-0 px-5 pb-3 pt-5 sm:px-6 sm:pt-6">
          <DialogTitle className="break-words pr-7">
            {showOnce ? 'Save this token now' : `Copy curl for ${keyName}`}
          </DialogTitle>
          <DialogDescription className="break-words">
            {showOnce
              ? `This is the only time the token for ${keyName} will be shown.`
              : 'Identity never stores the token, so paste the key into the placeholder before running the command.'}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-4 overflow-y-auto px-5 pb-5 sm:px-6">
          {showOnce ? (
            <div className="flex gap-2 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              Copy the token or a token-bearing curl command before closing this dialog.
            </div>
          ) : null}

          {issuedToken ? (
            <section className="grid min-w-0 gap-1.5">
              <h3 className="text-xs font-semibold text-ink">Token</h3>
              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md border border-border bg-bg-sunk px-3 py-2">
                <code className="block min-w-0 overflow-x-auto whitespace-nowrap font-mono text-xs text-ink">
                  {issuedToken.accessToken}
                </code>
                <CopyToClipboard
                  key={`token-${issuedToken.id}`}
                  textToCopy={issuedToken.accessToken}
                  onCopySuccess={() => setCopiedTokenId(issuedToken.id)}
                />
              </div>
            </section>
          ) : null}

          {showIntakeCommand ? (
            <section className="grid min-w-0 gap-2">
              <div>
                <h3 className="text-xs font-semibold text-ink">Intake upload command</h3>
                <p className="text-11 text-ink-3">Choose an intake this key can access.</p>
              </div>
              {intakes.length > 0 ? (
                <>
                  <Select value={selectedIntake} onValueChange={setSelectedIntake}>
                    <SelectTrigger className="w-full min-w-0">
                      <SelectValue placeholder="Choose an intake" />
                    </SelectTrigger>
                    <SelectContent>
                      {intakes.map((intake) => (
                        <SelectItem key={intake.id} value={intake.name}>
                          {intake.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2 rounded-md border border-border bg-bg-sunk px-3 py-2">
                    <pre className="block min-w-0 max-w-full overflow-x-auto whitespace-pre font-mono text-11 leading-relaxed text-ink">
                      {command}
                    </pre>
                    <CopyToClipboard
                      key={`curl-${issuedToken?.id ?? 'placeholder'}-${selectedIntake}`}
                      textToCopy={command}
                      onCopySuccess={() => {
                        if (issuedToken) setCopiedTokenId(issuedToken.id)
                      }}
                    />
                  </div>
                </>
              ) : (
                <p className="rounded-md border border-border bg-bg-sunk px-3 py-2 text-xs text-ink-3">
                  No intakes exist for this access yet.
                </p>
              )}
            </section>
          ) : null}
        </div>

        <DialogFooter className="border-t border-border px-5 py-3 sm:px-6">
          <Button type="button" disabled={!canClose} onClick={close}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
